import {
  Box,
  Center,
  Flex,
  FormLabel,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { currencyFormat, Number, numberFormat } from 'components/Number';
import ChartTooltip from 'components/charts/ChartTooltip';
import SelectInput from 'components/inputs/SelectInput';
import { generateSelectOptions } from 'helpers/inputHelper';
import { toScientificNotation } from 'helpers/numberHelper';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import regression from 'regression';
import { getCurrentMonthDiscreteMarkers } from './predictionChartMarkers';
import { buildLinearDeviationBandSeries } from './regressionDeviationBand';

const REGRESSION_TYPE = {
  LINEAR: 'linear',
  EXPONENTIAL: 'exponential',
  LOGARITHMIC: 'logarithmic',
  POWER: 'power',
  POLYNOMIAL: 'polynomial',
};

const REGRESSION_TYPE_META = {
  [REGRESSION_TYPE.LINEAR]: {
    label: 'Linear',
  },
  [REGRESSION_TYPE.EXPONENTIAL]: {
    label: 'Exponential',
  },
  [REGRESSION_TYPE.LOGARITHMIC]: {
    label: 'Logarithmic',
  },
  [REGRESSION_TYPE.POWER]: {
    label: 'Power',
  },
  [REGRESSION_TYPE.POLYNOMIAL]: {
    label: 'Polynomial',
  },
};

const getRegressionData = (regressionType, data) => {
  data = data.filter((point) => point[0] > 0 && point[1] > 0);

  let regressionFunc = null;
  switch (regressionType) {
    case REGRESSION_TYPE.LINEAR:
      regressionFunc = regression.linear;
      break;
    case REGRESSION_TYPE.EXPONENTIAL:
      regressionFunc = regression.exponential;
      break;
    case REGRESSION_TYPE.LOGARITHMIC:
      regressionFunc = regression.logarithmic;
      break;
    case REGRESSION_TYPE.POWER:
      regressionFunc = regression.power;
      break;
    case REGRESSION_TYPE.POLYNOMIAL:
      regressionFunc = regression.polynomial;
      break;
    default:
      regressionFunc = regression.linear;
      break;
  }
  return regressionFunc(data, {
    precision: 10,
  });
};

export const toScientificNotation5 = (num) => {
  if (num === 0) {
    return '0';
  }

  let exponent = Math.floor(Math.log10(Math.abs(num)));
  let mantissa = num / Math.pow(10, exponent);

  // Formatting to remove trailing zeros
  let formattedMantissa = parseFloat(mantissa.toFixed(5));

  return {
    exponent,
    mantissa: formattedMantissa,
  }
};

const formatRegressionEquation = (regressionData, regressionType) => {
  const { equation } = regressionData;

  if (regressionType === REGRESSION_TYPE.LINEAR) {
    const gradient = equation[0];
    const yIntercept = equation[1];

    const { mantissa, exponent } = toScientificNotation5(gradient);

    const x = `${mantissa}/${numberFormat(Math.pow(10, Math.abs(exponent)-2))}`;
    const y = numberFormat(yIntercept, { maximumFractionDigits: 5 });

    return `y(%) = ${x}*x + ${y}`;
  }

  return regressionData.string;
};

const calculateRoasSensitivity = (gradient) => {
  return 1 / gradient / 100; // Convert to percentage
};

const UaRoasPredictionChart = ({ cohortsData }) => {

  const [chartData, setChartData] = useState(null);
  const [regressionType, setRegressionType] = useState(REGRESSION_TYPE.LINEAR);
  const [cohortNumber, setCohortNumber] = useState(0);

  useEffect(() => {
    if (cohortsData) {
      const chartData = generateChartData();
      setChartData(chartData);
    }
  }, [cohortsData, regressionType, cohortNumber]);

  const generateChartData = () => {

    const cohortRows = cohortsData.rows.filter(
      (row) => row.data.roasByArpu.length > cohortNumber
    );

    const cacToUaSeriesData = cohortRows.map((row) => {
      return [
        row.data.totalUa,
        row.data.roasByArpu[cohortNumber].value, // Convert to percentage
      ]
    });

    const regressionData = getRegressionData(regressionType, cacToUaSeriesData);

    const regressionSeriesData = regressionData.points.sort(
      (a, b) => a[0] - b[0]
    );

    const cacToUaSeries = {
      name: 'Roas To Ua',
      data: cacToUaSeriesData,
      type: 'scatter',
      color: '#775ffc',
    };

    const regressionSeries = {
      name: 'Regression',
      data: regressionSeriesData,
      type: 'line',
      color: '#c9fe37',
    };

    const series = [cacToUaSeries, regressionSeries];
    const deviationBand =
      regressionType === REGRESSION_TYPE.LINEAR
        ? buildLinearDeviationBandSeries(cacToUaSeriesData, regressionData)
        : null;
    if (deviationBand) {
      series.push(...deviationBand);
    }

    return {
      series,
      options: getChartOptions(
        getCurrentMonthDiscreteMarkers(cohortRows),
        Boolean(deviationBand)
      ),
      regressionData,
    };
  };

  const getChartOptions = (currentMonthMarkers, showDeviationBand) => {
    return {
      chart: {
        type: 'line',
        fontFamily: 'DM Sans',
        animations: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        shared: false,
        intersect: true,
        enabledOnSeries: showDeviationBand ? [0] : undefined,
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const point = w.config.series[0].data[dataPointIndex];

          const totalUa = point[0];
          const roas = point[1];

          const items = [
            {
              label: 'Roas',
              value: numberFormat(roas, { maximumFractionDigits: 3 }),
            },
            {
              label: 'Total UA',
              value: currencyFormat(totalUa),
            },
          ];

          return renderToString(
            <ChartTooltip items={items} hideMarkers={true} />
          );
        },
      },
      legend: {
        show: false,
      },
      fill: {
        type: 'solid',
      },
      ...(showDeviationBand
        ? {
            stroke: {
              curve: 'straight',
              width: [0, 2, 2, 2],
              dashArray: [0, 0, 6, 6],
            },
          }
        : {}),
      markers: {
        size: showDeviationBand ? [8, 0, 0, 0] : [8, 0],
        discrete: currentMonthMarkers,
      },
      yaxis: {
        title: {
          text: 'Roas',
        },
        labels: {
          formatter: (value) => {
            return numberFormat(value, { maximumFractionDigits: 3 });
          },
        },
      },
      xaxis: {
        title:{
          text: 'Total UA',
        },
        tooltip: {
          enabled: false,
        },
        labels: {
          formatter: (value) => {
            return currencyFormat(value);
          },
        },
      },
    };
  };

  if (!chartData) {
    return null;
  }

  function generateCohortNumberOptions(length) {
    let r =  Array.from({length: length}, (_, i) => {
      let r2 = {
        label: `${i}`,
        value: i,
      };
      return r2;
    });
    return r;
  }

  return (
    <>
      <Flex justifyContent={'space-between'} alignItems={'center'}>
        <Text fontWeight={'800'}>Roas Prediction</Text>
        <Flex align={'center'} gap={1}>
          <FormLabel m={0}>Regression: </FormLabel>
          <SelectInput
            options={generateSelectOptions({ metaObj: REGRESSION_TYPE_META })}
            value={regressionType}
            onChange={setRegressionType}
            w={200}
          />
          <FormLabel m={0}>Period number:</FormLabel>
          <SelectInput
              options={generateCohortNumberOptions(cohortsData.rows.length)}
              value={cohortNumber}
              onChange={setCohortNumber}
              w={200}
          />
        </Flex>
      </Flex>
      <Box mt={4}>
        <Box>
          <Center>
            <SimpleGrid columns={2} spacingY={2} spacingX={10}>
              <Text>
                <b>Equation: </b>{' '}
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatRegressionEquation(
                      chartData.regressionData,
                      regressionType
                    ),
                  }}
                ></span>
              </Text>
              <Text>
                <b>
                  R<sup>2</sup>
                </b>{' '}
                : {chartData.regressionData.r2}
              </Text>
              {regressionType === REGRESSION_TYPE.LINEAR && (
                <>
                  <Text>
                    <b>Roas Sensitivity: </b>
                    <Number
                      value={calculateRoasSensitivity(
                        chartData.regressionData.equation[0]
                      )}
                    />
                  </Text>
                  <Box></Box>
                </>
              )}
            </SimpleGrid>
          </Center>
        </Box>
        <Box h={350} mt={-4}>
          <Chart
            type="line"
            width={'100%'}
            height={'100%'}
            series={chartData.series}
            options={chartData.options}
          />
        </Box>
      </Box>
    </>
  );
};

UaRoasPredictionChart.propTypes = {
  cohortsData: PropTypes.object,
};

export default UaRoasPredictionChart;
